import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import CheckoutLayout from "@/components/CheckoutLayout";
import api from "@/api";
import { useAuth } from "@/context/AuthContext";
import { fetchCatalog, formatPrice } from "@/utils/checkout";
import { CheckCircle2, ArrowRight, Loader2, ShieldCheck, Sparkles, Receipt, Mail, Download } from "lucide-react";

// Friendly name fallback if catalog fetch fails
const FALLBACK_NAME = {
  studio: "Viral Book Studio",
  suite: "The Viral Book Suite",
  vip: "Viral Book Suite VIP",
  cohort: "Evergreen Monthly Income Cohort",
  dfy: "DFY Evergreen Installation",
  cohort_plan: "Cohort — 3-month Plan",
  dfy_plan: "DFY — 3-month Plan",
};

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); }
  catch { return iso; }
}

export default function OrderSummary() {
  const nav = useNavigate();
  const { user, refresh } = useAuth();

  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState([]);
  const [catalog, setCatalog] = useState({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Re-pull entitlements so the dashboard CTA only fires when we know
        // the user genuinely owns at least one tier.
        try { await refresh(); } catch (_) {}
        const [hist, cat] = await Promise.all([
          api.get("/api/payments/history").then((r) => r.data || []).catch(() => []),
          fetchCatalog().catch(() => ({ tiers: {} })),
        ]);
        if (cancelled) return;
        // Only show paid rows; ignore admin grants & pending
        const paid = (hist || []).filter(
          (t) => (t.payment_status === "paid" || t.status === "complete") && t.kind !== "admin_grant"
        );
        setHistory(paid);
        setCatalog(cat?.tiers || {});
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [refresh]);

  const total = history.reduce((s, t) => s + (Number(t.amount) || 0), 0);
  const purchases = (user?.purchases || []).filter((p) => p !== "admin_grant");
  const entitlements = user?.entitlements || [];

  return (
    <CheckoutLayout>
      <div className="vbs-upsell-hero" data-testid="order-summary-hero">
        <div className="ok"><CheckCircle2 size={14} /> Order Complete</div>
        <h1>You're <em>all set.</em></h1>
        <p>
          Thank you for your order. Everything you've purchased is unlocked
          and ready to use right now. A receipt has been emailed to{" "}
          <strong style={{ color: "var(--gold)" }}>{user?.email || "your account"}</strong>.
        </p>
      </div>

      <section className="mkt-section" style={{ paddingTop: 0, paddingBottom: 30 }}>
        <div className="vbs-offer-big" data-testid="order-summary-card">
          <span className="badge"><Sparkles size={11} style={{ verticalAlign: "-1px", marginRight: 4 }} /> Order Summary</span>
          <div className="pitch-eye">Itemised receipt</div>

          {loading ? (
            <div style={{ padding: 26, textAlign: "center", color: "var(--gray)" }} data-testid="order-summary-loading">
              <Loader2 className="animate-spin" size={18} style={{ marginRight: 8, verticalAlign: "-3px" }} />
              Loading your order…
            </div>
          ) : history.length === 0 ? (
            <div style={{ padding: 26, color: "var(--gray)" }} data-testid="order-summary-empty">
              <p style={{ marginBottom: 14 }}>
                We don't see any completed purchases on this account yet.
                If you've just paid and this looks wrong, give Stripe a few seconds
                and refresh this page.
              </p>
            </div>
          ) : (
            <>
              <div style={{ display: "flex", flexDirection: "column", gap: 10, margin: "20px 0 18px" }}>
                {history.map((t) => {
                  const tier = catalog[t.tier_id];
                  const name = tier?.name || FALLBACK_NAME[t.tier_id] || t.tier_id;
                  const isPlan = t.kind === "subscription_3x";
                  return (
                    <div
                      key={t.session_id}
                      style={{
                        display: "flex", alignItems: "center", gap: 14,
                        padding: "14px 18px",
                        border: "1px solid var(--border)", borderRadius: 4,
                        background: "rgba(201,168,76,0.04)",
                      }}
                      data-testid={`order-line-${t.session_id}`}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ color: "var(--bright)", fontWeight: 500, fontSize: 14 }}>
                          {name}
                          {isPlan && (
                            <span style={{ marginLeft: 8, fontSize: 10, color: "var(--gold)", letterSpacing: 1.5, textTransform: "uppercase" }}>
                              3-month plan
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: 11.5, color: "var(--gray)", marginTop: 3, fontFamily: "var(--font-mono, monospace)" }}>
                          {fmtDate(t.created_at)} · {(t.currency || "usd").toUpperCase()}
                          {t.one_click ? " · ONE-CLICK" : ""}
                        </div>
                      </div>
                      <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 18, color: "var(--gold)", fontWeight: 600 }}>
                        {formatPrice(t.amount)}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div
                style={{
                  display: "flex", justifyContent: "space-between", alignItems: "baseline",
                  padding: "14px 18px", marginBottom: 24,
                  borderTop: "1px dashed rgba(201,168,76,0.32)",
                }}
                data-testid="order-summary-total"
              >
                <span style={{ color: "var(--bright)", fontFamily: "'Cormorant Garamond', serif", fontSize: 20, fontWeight: 500 }}>
                  Total paid today
                </span>
                <span style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 32, color: "var(--gold)", fontWeight: 700 }}>
                  {formatPrice(total)}
                </span>
              </div>
            </>
          )}

          {entitlements.length > 0 && (
            <div style={{ marginBottom: 22 }}>
              <div style={{ fontSize: 11, letterSpacing: 2, color: "var(--gold-dim)", textTransform: "uppercase", marginBottom: 8 }}>
                What's now unlocked
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {entitlements.map((e) => (
                  <span
                    key={e}
                    style={{
                      padding: "5px 12px", borderRadius: 999,
                      background: "rgba(45,184,145,0.08)",
                      border: "1px solid rgba(45,184,145,0.32)",
                      color: "#7be0ad", fontSize: 11.5, letterSpacing: 0.5,
                      textTransform: "capitalize",
                    }}
                    data-testid={`entitlement-${e}`}
                  >
                    {e.replace("_", " ")}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", justifyContent: "center" }}>
            <button
              onClick={() => nav("/dashboard")}
              className="mkt-btn mkt-btn-primary"
              disabled={entitlements.length === 0}
              data-testid="open-dashboard"
              title={entitlements.length === 0 ? "Complete a purchase to unlock the dashboard" : "Open My Dashboard"}
            >
              Open My Dashboard <ArrowRight size={14} />
            </button>
            <button
              onClick={() => nav("/settings")}
              className="mkt-btn mkt-btn-ghost"
              data-testid="open-settings"
            >
              <Receipt size={13} /> View Receipts in Settings
            </button>
          </div>

          <p className="vbs-upsell-trust" style={{ marginTop: 20 }}>
            <Mail size={12} /> Receipt sent to {user?.email || "your email"} <span style={{ margin: "0 6px" }}>·</span>
            <Download size={12} /> Stripe receipts available in Settings <span style={{ margin: "0 6px" }}>·</span>
            <ShieldCheck size={12} /> 14-day guarantee
          </p>
        </div>
      </section>
    </CheckoutLayout>
  );
}
